import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogOut, ShieldCheck, FileText,
         Clock as ClockIcon, ShoppingBag, TrendingUp, CheckCircle2, Boxes,
         ClipboardList, Users, ChevronDown, MapPinned } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer } from "recharts";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import RoutePlanCard from "@/pages/field/RoutePlanCard";
import MyTargetsCard from "@/pages/field/MyTargetsCard";
import { fmtHm } from "@/lib/hours";


export default function MobileHome() {
  const { user, visibility, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const roleLabel = user?.role_code === "SE" ? "Sales Executive" : user?.role_code === "DISTRIBUTOR" ? "Distributor" : user?.role_code;
  const isSE = user?.role_code === "SE";
  // ASM works the field themselves but also manages a team of SEs — their
  // Home shows the team's live today status instead of (or alongside) a
  // personal one, since ASM rarely books visits/orders themselves.
  const isASM = user?.role_code === "ASM";

  return (
    <div className="px-4 pt-4 pb-24 space-y-4" data-testid="mobile-home">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-400">Welcome</div>
        <div className="text-2xl font-extrabold tracking-tight text-neutral-900 leading-tight">
          Hi, <span className="tulsi-text-gradient">{user?.name?.split(" ")[0] || "there"}</span>
        </div>
        <div className="text-sm text-neutral-500 mt-1">Let's get today's work started.</div>
      </div>

      <Card className="border-none rounded-2xl overflow-hidden tulsi-gradient text-white shadow-lg shadow-[#E5197F]/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-white/70">Role</div>
              <div className="font-semibold">{roleLabel}</div>
            </div>
          </div>
          {!isSE && (
            <div className="mt-4 grid grid-cols-2 gap-3 text-white/95">
              <div className="rounded-xl bg-white/10 backdrop-blur px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/70">Zones</div>
                <div className="text-sm font-semibold" data-testid="mobile-zones-count">
                  {visibility?.is_global ? "All" : visibility?.zone_ids?.length ?? user?.assigned_zone_ids?.length ?? 0}
                </div>
              </div>
              <div className="rounded-xl bg-white/10 backdrop-blur px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/70">Team visible</div>
                <div className="text-sm font-semibold" data-testid="mobile-team-count">
                  {visibility?.is_global ? "All" : visibility?.user_ids?.length ?? 1}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drop 9a · Item 8 — Today's Dashboard replaces Zones/Team tiles for SE.
          ASM also punches in/visits themselves, so they get their own
          dashboard too — plus the team one, since they manage a team. */}
      {(isSE || isASM) && <TodayDashboardCard mine={isASM} />}
      {isASM && <TeamTodayDashboardCard />}

      {isSE && <RoutePlanCard />}
      {(isSE || isASM) && (
        <Button
          variant="outline"
          onClick={() => navigate("/today-activity")}
          data-testid="mobile-today-activity-cta"
          className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5"
        >
          <ShoppingBag className="h-4 w-4 mr-3 text-[#6D2C91]" />
          <span className="flex-1 text-left">Today's Activity</span>
          <span className="text-xs text-neutral-400">Visits · Orders · Forms</span>
        </Button>
      )}
      {(isSE || isASM) && (
        <Button
          variant="outline"
          onClick={() => navigate("/distributor-stock")}
          data-testid="mobile-distributor-stock-cta"
          className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5"
        >
          <Boxes className="h-4 w-4 mr-3 text-[#6D2C91]" />
          <span className="flex-1 text-left">Distributor Stock</span>
          <span className="text-xs text-neutral-400">Check availability</span>
        </Button>
      )}
      {isSE && <MyTargetsCard />}

      {(isSE || isASM) && (
        <Button
          variant="outline"
          onClick={() => navigate("/forms")}
          data-testid="mobile-forms-cta"
          className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5"
        >
          <FileText className="h-4 w-4 mr-3 text-[#6D2C91]" />
          <span className="flex-1 text-left">Activity Forms</span>
          <span className="text-xs text-neutral-400">Fill</span>
        </Button>
      )}
      {isSE && (
        <Button
          variant="outline"
          onClick={() => navigate("/field/my-day-summary")}
          data-testid="mobile-day-summary-cta"
          className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5"
        >
          <ClipboardList className="h-4 w-4 mr-3 text-[#6D2C91]" />
          <span className="flex-1 text-left">Daily Working Summary</span>
          <span className="text-xs text-neutral-400">Past days</span>
        </Button>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          onClick={() => navigate("/change-password")}
          data-testid="mobile-change-password"
          className="h-12 rounded-xl border-neutral-200 font-medium"
        >
          <KeyRound className="h-4 w-4 mr-2" /> Password
        </Button>
        <Button
          variant="outline"
          onClick={onLogout}
          data-testid="mobile-logout"
          className="h-12 rounded-xl border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50 font-medium"
        >
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drop 9a · Item 8 · TodayDashboardCard
// Fetches /api/home/today_dashboard on mount + window focus; renders 9 tiles
// covering attendance, calls, and productive KG. Null values render `—`.
// ---------------------------------------------------------------------------
function TodayDashboardCard({ mine = false }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/home/today_dashboard");
      setD(data);
      setErr(null);
    } catch (e) { setErr(e?.response?.data?.detail || e.message || "load failed"); }
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const dt = new Date(iso);
      return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit",
                                          hour12: true });
    } catch { return "—"; }
  };
  const fmtHours = (h) => fmtHm(h);
  const fmtKg = (k) => (k == null ? "—" : `${Number(k).toFixed(1)}`);
  const fmtN = (n) => (n == null ? "—" : String(n));

  const planned = d?.total_calls_planned_today ?? 0;
  const visited = d?.total_unique_visited_today ?? 0;
  const productive = d?.total_unique_productive_today ?? 0;
  const callsPct = planned > 0 ? Math.min(1, visited / planned) : 0;
  const productivePct = visited > 0 ? Math.min(1, productive / visited) : 0;

  const kgAvg90d = d?.beat_avg_kg_per_call_3mo ?? null;
  const kgToday = d?.total_kg_productive_today ?? null;
  const kgAvgToday = visited > 0 && kgToday != null ? kgToday / visited : null;
  const trendData = useMemo(() => ([
    { name: "90d avg", kg: kgAvg90d != null ? Number(kgAvg90d.toFixed(1)) : 0 },
    { name: "Today", kg: kgAvgToday != null ? Number(kgAvgToday.toFixed(1)) : 0 },
  ]), [kgAvg90d, kgAvgToday]);
  const aboveAvg = kgAvg90d != null && kgAvgToday != null && kgAvgToday >= kgAvg90d;

  return (
    <Card className="border-neutral-200 rounded-2xl overflow-hidden"
          data-testid="home-dash-card">
      <CardContent className="p-4 space-y-4">
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          {mine ? "My day — today" : "Today's Dashboard"}
        </div>
        {err && (
          <div className="text-xs text-red-600">
            {typeof err === "string" ? err : "Failed to load"}
          </div>
        )}

        {!d && !err ? (
          <div className="animate-pulse space-y-3">
            <div className="h-24 bg-neutral-100 rounded-xl" />
            <div className="h-16 bg-neutral-100 rounded-xl" />
          </div>
        ) : (
          <>
            {/* Hero: calls-today progress ring + productive-conversion bar */}
            <div className="flex items-center gap-4">
              <div className="relative shrink-0" data-testid="home-dash-calls-ring">
                <ProgressRing pct={callsPct} size={92} strokeWidth={9} gradientId="ring-calls" />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-lg font-extrabold text-neutral-900 leading-none">
                    {fmtN(visited)}<span className="text-neutral-400 font-semibold">/{fmtN(planned)}</span>
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-400 mt-0.5">Calls</div>
                </div>
              </div>
              <div className="flex-1 space-y-2.5">
                <div>
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="text-neutral-500 font-medium">Productive conversion</span>
                    <span className="font-bold text-neutral-900" data-testid="home-dash-productive-pct">
                      {productive}/{visited || 0}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full rounded-full tulsi-gradient transition-all"
                         style={{ width: `${Math.round(productivePct * 100)}%` }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <MiniStat icon={ClockIcon} label="Punch-in" value={fmtTime(d?.punch_in_ts)} testid="home-dash-punch-in" />
                  <MiniStat icon={ClockIcon} label="Hours" value={fmtHours(d?.working_hours_today)} testid="home-dash-working-hours" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <MiniStat icon={CheckCircle2} label="First call" value={fmtTime(d?.first_call_ts)} testid="home-dash-first-call" />
              <MiniStat icon={CheckCircle2} label="Last call" value={fmtTime(d?.last_call_ts)} testid="home-dash-last-call" />
            </div>

            {/* KG-per-call trend: today vs trailing 90-day beat average */}
            <div className="rounded-xl border border-neutral-200 p-3" data-testid="home-dash-kg-trend">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-400">
                  <TrendingUp className="h-3 w-3" /> KG / call vs 90d avg
                </div>
                <div className="text-sm font-bold" data-testid="home-dash-kg-today">
                  {fmtKg(kgToday)} <span className="text-[10px] font-normal text-neutral-400">kg today</span>
                </div>
              </div>
              <div style={{ width: "100%", height: 72 }}>
                <ResponsiveContainer>
                  <BarChart data={trendData} layout="vertical"
                            margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={54}
                           tick={{ fontSize: 11, fill: "#737373" }}
                           axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => [`${v} kg`, "avg / call"]}
                             contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Bar dataKey="kg" radius={[6, 6, 6, 6]} barSize={16}>
                      <Cell fill="#d4d4d8" />
                      <Cell fill={aboveAvg ? "#059669" : "#E5197F"} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TeamTodayDashboardCard — ASM's Home. Live per-reportee "today" snapshot
// (punch-in, calls visited/productive, kg sold), fetched from
// GET /api/home/team_today_dashboard (backend/routes_home_dashboard.py).
// ---------------------------------------------------------------------------
function fmtClockTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return "—"; }
}

const ROLE_LABELS = { SE: "SE", ASM: "ASM", ZSM: "ZSM", RSM: "RSM", DISTRIBUTOR: "Distributor" };
function roleLabel(code) { return ROLE_LABELS[code] || code; }

// A single highlighted number with icon + label — used for the team's
// aggregate totals strip. `accent` tints the icon chip.
function TeamStat({ icon: Icon, label, value, accent = "#6D2C91" }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-neutral-50/80 border border-neutral-100 px-2.5 py-2">
      <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
           style={{ backgroundColor: `${accent}1a`, color: accent }}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-neutral-900 leading-tight truncate">{value}</div>
        <div className="text-[9px] uppercase tracking-wider text-neutral-400 leading-tight">{label}</div>
      </div>
    </div>
  );
}

// Live work-status pill for one SE: not started / working / day ended.
function StatusPill({ t }) {
  if (t.day_ended) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500">
        <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" /> Day ended
      </span>
    );
  }
  if (t.punch_in_ts) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Working
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Not started
    </span>
  );
}

// Expanded per-SE detail — the same fields SE's own TodayDashboardCard
// shows, just as a compact stat grid instead of the ring visualization.
function TeamMemberDetail({ t }) {
  return (
    <div className="px-3 pb-3 pt-1 space-y-2" data-testid={`team-home-dash-detail-${t.user_id.slice(0, 8)}`}>
      <div className="grid grid-cols-3 gap-1.5">
        <MiniStat icon={ClockIcon} label="Start call" value={fmtClockTime(t.first_call_ts)} />
        <MiniStat icon={ClockIcon} label="End call" value={fmtClockTime(t.last_call_ts)} />
        <MiniStat icon={ClockIcon} label="Punch out" value={fmtClockTime(t.punch_out_ts)} />
        <MiniStat icon={CheckCircle2} label="TC" value={t.total_unique_visited_today} />
        <MiniStat icon={CheckCircle2} label="PC" value={t.total_unique_productive_today} />
        <MiniStat icon={MapPinned} label="Total call of Beat" value={t.beat_total_calls_today || 0} />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <MiniStat icon={TrendingUp} label="KG today" value={t.total_kg_productive_today} />
        <MiniStat icon={ClockIcon} label="Hours" value={fmtHm(t.working_hours_today)} />
      </div>
      <div className="rounded-xl border border-neutral-200 px-3 py-2">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-neutral-500 font-medium flex items-center gap-1">
            <MapPinned className="h-3 w-3" /> Beat coverage
          </span>
          <span className="font-bold text-neutral-900">
            {t.total_unique_visited_today}/{t.beat_total_calls_today || 0} · {t.beat_coverage_pct}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full rounded-full tulsi-gradient transition-all"
               style={{ width: `${Math.min(100, t.beat_coverage_pct)}%` }} />
        </div>
      </div>
    </div>
  );
}

function TeamTodayDashboardCard() {
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [err, setErr] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  // Role-wise tabs — an ASM's reporting tree can include 60-70 mapped
  // distributor logins alongside their ~20 SE/ASM reports; without a filter
  // this list becomes one long undifferentiated scroll. Options are built
  // from whatever roles actually show up in `team`, not a hardcoded list.
  const [roleFilter, setRoleFilter] = useState("ALL");
  // A long team no longer stretches the whole page: the list scrolls inside the card, can be
  // searched by name, and the whole card can be minimised.
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(() => {
    try { return sessionStorage.getItem("tulsi:team-card-collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    try { sessionStorage.setItem("tulsi:team-card-collapsed", c ? "0" : "1"); } catch { /* ignore */ }
    return !c;
  });

  const load = () => {
    api.get("/home/team_today_dashboard")
      .then(({ data }) => { setTeam(data.team || []); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || e.message || "load failed"));
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const initials = (name) => (name || "?")
    .split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  const roleTabs = useMemo(() => {
    if (!team) return [];
    const counts = {};
    for (const t of team) counts[t.role_code] = (counts[t.role_code] || 0) + 1;
    return Object.keys(counts).sort().map((code) => ({ code, count: counts[code] }));
  }, [team]);

  const visibleTeam = useMemo(() => {
    if (!team) return [];
    const byRole = roleFilter === "ALL" ? team : team.filter((t) => t.role_code === roleFilter);
    const q = search.trim().toLowerCase();
    return q ? byRole.filter((t) => (t.name || "").toLowerCase().includes(q)) : byRole;
  }, [team, roleFilter, search]);

  // Recompute the summary strip for whichever tab is active — same math as
  // the backend's overall `totals`, just over `visibleTeam` instead of the
  // whole team, so picking "Distributor" shows distributor-only figures.
  const visibleTotals = useMemo(() => {
    if (!visibleTeam.length) return null;
    const avgTs = (isoList) => {
      if (!isoList.length) return null;
      const avgMs = isoList.reduce((s, iso) => s + new Date(iso).getTime(), 0) / isoList.length;
      return new Date(avgMs).toISOString();
    };
    const hours = visibleTeam.map((t) => t.working_hours_today).filter((v) => v != null);
    return {
      total_kg_productive_today: Math.round(
        visibleTeam.reduce((s, t) => s + (t.total_kg_productive_today || 0), 0) * 100) / 100,
      total_tc: visibleTeam.reduce((s, t) => s + (t.total_unique_visited_today || 0), 0),
      total_pc: visibleTeam.reduce((s, t) => s + (t.total_unique_productive_today || 0), 0),
      avg_start_call_ts: avgTs(visibleTeam.map((t) => t.first_call_ts).filter(Boolean)),
      avg_end_call_ts: avgTs(visibleTeam.map((t) => t.last_call_ts).filter(Boolean)),
      avg_working_hours: hours.length
        ? Math.round((hours.reduce((s, h) => s + h, 0) / hours.length) * 100) / 100
        : null,
    };
  }, [visibleTeam]);

  return (
    <Card className="border-neutral-200 rounded-2xl overflow-hidden" data-testid="team-home-dash-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-neutral-400 flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> Team — today
          </div>
          <div className="flex items-center gap-3">
            <button className="text-[11px] font-semibold text-[#6D2C91]"
                    onClick={() => navigate("/manager/team-day-summaries")}
                    data-testid="team-home-dash-view-all">
              Day-by-day →
            </button>
            <button type="button" onClick={toggleCollapsed} aria-label={collapsed ? "Expand team" : "Minimise team"}
                    className="h-7 w-7 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                    data-testid="team-home-dash-toggle">
              <ChevronDown className={"h-4 w-4 transition-transform " + (collapsed ? "" : "rotate-180")} />
            </button>
          </div>
        </div>
        {collapsed && team && (
          <button type="button" onClick={toggleCollapsed} className="w-full text-left text-xs text-neutral-500">
            {team.length} team member{team.length === 1 ? "" : "s"} · tap to expand
          </button>
        )}

        {err && <div className="text-xs text-red-600">{typeof err === "string" ? err : "Failed to load"}</div>}

        {!collapsed && visibleTotals && (
          <div className="grid grid-cols-2 gap-1.5" data-testid="team-home-dash-totals">
            <TeamStat icon={TrendingUp} label="Total KG" value={visibleTotals.total_kg_productive_today} accent="#E5197F" />
            <TeamStat icon={CheckCircle2} label="Total PC / TC" value={`${visibleTotals.total_pc}/${visibleTotals.total_tc}`} accent="#059669" />
            <TeamStat icon={ClockIcon} label="Avg start" value={fmtClockTime(visibleTotals.avg_start_call_ts)} accent="#6D2C91" />
            <TeamStat icon={ClockIcon} label="Avg end" value={fmtClockTime(visibleTotals.avg_end_call_ts)} accent="#6D2C91" />
            <TeamStat icon={ClockIcon} label="Avg hours"
                       value={fmtHm(visibleTotals.avg_working_hours)}
                       accent="#0284c7" />
          </div>
        )}

        {!collapsed && team && team.length > 6 && (
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search team member…"
                 className="w-full h-9 rounded-lg border border-neutral-200 px-3 text-sm outline-none focus:border-[#6D2C91]"
                 data-testid="team-home-dash-search" />
        )}

        {!collapsed && team && team.length > 0 && roleTabs.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid="team-home-dash-role-tabs">
            <button type="button" onClick={() => setRoleFilter("ALL")}
                    data-testid="team-home-dash-role-tab-all"
                    className={"text-[11px] font-medium px-2.5 py-1 rounded-full border " +
                      (roleFilter === "ALL" ? "border-[#6D2C91] bg-[#6D2C91]/10 text-[#6D2C91]"
                                            : "border-neutral-200 text-neutral-600")}>
              All ({team.length})
            </button>
            {roleTabs.map(({ code, count }) => (
              <button key={code} type="button" onClick={() => setRoleFilter(code)}
                      data-testid={`team-home-dash-role-tab-${code}`}
                      className={"text-[11px] font-medium px-2.5 py-1 rounded-full border " +
                        (roleFilter === code ? "border-[#6D2C91] bg-[#6D2C91]/10 text-[#6D2C91]"
                                             : "border-neutral-200 text-neutral-600")}>
                {roleLabel(code)} ({count})
              </button>
            ))}
          </div>
        )}

        {collapsed ? null : !team && !err ? (
          <div className="animate-pulse space-y-2">
            <div className="h-14 bg-neutral-100 rounded-xl" />
            <div className="h-14 bg-neutral-100 rounded-xl" />
          </div>
        ) : team && team.length === 0 ? (
          <div className="text-xs text-neutral-400 py-3 text-center">No team members visible.</div>
        ) : visibleTeam.length === 0 ? (
          <div className="text-xs text-neutral-400 py-3 text-center">No {roleLabel(roleFilter)} in team.</div>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1 -mr-1" data-testid="team-home-dash-list">
            {visibleTeam.map((t) => {
              const isOpen = expandedId === t.user_id;
              return (
                <div key={t.user_id}
                     className={"rounded-xl border overflow-hidden transition-colors " +
                       (isOpen ? "border-[#6D2C91]/30 bg-[#6D2C91]/[0.03]" : "border-neutral-100 bg-neutral-50/60")}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left"
                    onClick={() => setExpandedId((id) => id === t.user_id ? null : t.user_id)}
                    data-testid={`team-home-dash-row-${t.user_id.slice(0, 8)}`}>
                    <div className="h-9 w-9 rounded-full tulsi-gradient text-white flex items-center justify-center text-[11px] font-bold shrink-0">
                      {initials(t.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-neutral-900 truncate">{t.name || "—"}</div>
                      <StatusPill t={t} />
                    </div>
                    <div className="text-right shrink-0 pl-1">
                      <div className="text-xs font-bold text-neutral-900">
                        {t.total_unique_productive_today}/{t.total_unique_visited_today}
                        <span className="text-[9px] font-normal text-neutral-400 ml-1">calls</span>
                      </div>
                      <div className="text-[10px] text-neutral-500">{t.total_kg_productive_today} kg</div>
                    </div>
                    <ChevronDown className={"h-4 w-4 text-neutral-400 shrink-0 transition-transform " +
                      (isOpen ? "rotate-180" : "")} />
                  </button>
                  {isOpen && <TeamMemberDetail t={t} />}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProgressRing({ pct, size = 88, strokeWidth = 9, gradientId }) {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(1, Math.max(0, pct)));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E5197F" />
          <stop offset="100%" stopColor="#6D2C91" />
        </linearGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#f1f0f4" strokeWidth={strokeWidth} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={`url(#${gradientId})`} strokeWidth={strokeWidth}
              fill="none" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
    </svg>
  );
}

function MiniStat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 px-2.5 py-2"
          data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-neutral-400">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="text-sm font-semibold text-neutral-900 mt-0.5 truncate">
        {value}
      </div>
    </div>
  );
}
